import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'

interface Invoice {
  id: string
  number: string
  customer_name?: string
  customer_email?: string
}

interface SendInvoiceEmailDialogProps {
  invoice: Invoice | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: {
    recipient: string
    subject: string
    message: string
  }) => void
}

export function SendInvoiceEmailDialog({
  invoice,
  open,
  onOpenChange,
  onSubmit,
}: SendInvoiceEmailDialogProps) {
  const [emailRecipient, setEmailRecipient] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')

  // Initialize email data when invoice changes
  useState(() => {
    if (invoice) {
      setEmailRecipient(invoice.customer_email || '')
      setEmailSubject(`Invoice ${invoice.number}`)
      setEmailMessage(
        `Dear ${invoice.customer_name || 'Customer'},\n\nPlease find your invoice attached.\n\nBest regards,\nYour Team`
      )
    }
  })

  const handleSubmit = () => {
    onSubmit({
      recipient: emailRecipient,
      subject: emailSubject,
      message: emailMessage,
    })
    setEmailRecipient('')
    setEmailSubject('')
    setEmailMessage('')
    onOpenChange(false)
  }

  const handleClose = () => {
    setEmailRecipient('')
    setEmailSubject('')
    setEmailMessage('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Send Email</DialogTitle>
          <DialogDescription>
            {invoice && (
              <div>
                {invoice.customer_name && (
                  <>
                    Send invoice email to{' '}
                    <strong>{invoice.customer_name}</strong>
                    <br />
                  </>
                )}
                Invoice: {invoice.number}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='email-recipient'>Recipient</Label>
            <Input
              id='email-recipient'
              type='email'
              value={emailRecipient}
              onChange={e => setEmailRecipient(e.target.value)}
              placeholder='customer@example.com'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='email-subject'>Subject</Label>
            <Input
              id='email-subject'
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
              placeholder='Invoice subject'
            />
          </div>

          <div className='space-y-2'>
            <Label htmlFor='email-message'>Message</Label>
            <textarea
              id='email-message'
              className='w-full min-h-[120px] px-3 py-2 text-sm rounded-md border border-input bg-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
              value={emailMessage}
              onChange={e => setEmailMessage(e.target.value)}
              placeholder='Email message...'
            />
          </div>

          <div className='flex justify-end gap-3 pt-4'>
            <Button variant='outline' onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!emailRecipient || !emailSubject}
            >
              Send Email
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
